import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Zap, Mail, ArrowLeft, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { LanguageSwitch } from '../../i18n/LanguageSwitch'
import { useLocale } from '../../i18n/LocaleProvider'
import {
  forgotPassword,
  confirmForgotPassword,
  getCognitoErrorMessage,
  isUserNotFoundError,
} from '../../lib/cognito'

export function ForgotPassword() {
  const navigate = useNavigate()
  const { t } = useLocale()
  const [step, setStep] = useState('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  async function handleSendCode(e) {
    e.preventDefault()
    if (!email.trim()) { setError(t('auth.errors.requiredEmail')); return }
    setLoading(true)
    setError('')
    setNotice('')
    try {
      await forgotPassword(email.trim().toLowerCase())
      setStep('reset')
      setNotice(t('auth.forgotPassword.genericNotice'))
    } catch (err) {
      if (isUserNotFoundError(err)) {
        setStep('reset')
        setNotice(t('auth.forgotPassword.genericNotice'))
      } else {
        setError(getCognitoErrorMessage(err, t))
      }
    }
    setLoading(false)
  }

  async function handleReset(e) {
    e.preventDefault()
    if (!code.trim() || !newPassword) { setError(t('auth.errors.requiredFields')); return }
    if (newPassword.length < 8) { setError(t('auth.errors.passwordMinimum')); return }
    setLoading(true)
    setError('')
    try {
      await confirmForgotPassword(email, code.trim(), newPassword)
      setStep('done')
    } catch (err) {
      setError(getCognitoErrorMessage(err, t))
    }
    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-4 flex justify-end">
          <LanguageSwitch />
        </div>
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-12 h-12 rounded-2xl bg-brand-600 flex items-center justify-center shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-900 text-xl leading-tight">WorkflowPro</p>
              <p className="text-gray-500 text-sm leading-tight">{t('auth.brandTagline')}</p>
            </div>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card p-8">
          {step === 'email' && (
            <>
              <button onClick={() => navigate('/signin')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6">
                <ArrowLeft className="w-4 h-4" /> {t('auth.forgotPassword.back')}
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.forgotPassword.title')}</h1>
              <p className="text-gray-500 mb-6">{t('auth.forgotPassword.requestInstructions')}</p>
              {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4"><span className="text-red-600 text-sm font-medium">{error}</span></div>}
              <form onSubmit={handleSendCode} className="space-y-4">
                <div className="form-group">
                  <label className="label">{t('auth.common.email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="email" className="input pl-10" placeholder={t('auth.common.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
                  </div>
                </div>
                <Button type="submit" variant="primary" fullWidth loading={loading}>{t('auth.forgotPassword.sendCode')}</Button>
              </form>
            </>
          )}

          {step === 'reset' && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.forgotPassword.enterCode')}</h1>
              <p className="text-gray-500 mb-4">{t('auth.forgotPassword.codeInstructions')}</p>
              {notice && <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4"><span className="text-green-700 text-sm font-medium">{notice}</span></div>}
              {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4"><span className="text-red-600 text-sm font-medium">{error}</span></div>}
              <form onSubmit={handleReset} className="space-y-4">
                <div className="form-group">
                  <label className="label">{t('auth.forgotPassword.resetCode')}</label>
                  <input type="text" className="input text-center text-2xl tracking-widest font-bold" placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} autoFocus />
                </div>
                <div className="form-group">
                  <label className="label">{t('auth.forgotPassword.newPassword')}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type={showPassword ? 'text' : 'password'} className="input pl-10 pr-10" placeholder={t('auth.forgotPassword.newPasswordPlaceholder')} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={t(showPassword ? 'auth.common.hidePassword' : 'auth.common.showPassword')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" variant="primary" fullWidth loading={loading}>{t('auth.forgotPassword.resetAction')}</Button>
              </form>
            </>
          )}

          {step === 'done' && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.forgotPassword.doneTitle')}</h1>
              <p className="text-gray-500 mb-6">{t('auth.forgotPassword.doneBody')}</p>
              <Button variant="primary" fullWidth onClick={() => navigate('/signin')}>{t('auth.forgotPassword.signInNewPassword')}</Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}
